import { useEffect, useState } from 'react';
import { Button, DialogShell } from '@timely/ui';
import {
    ChequeStatus, chequeBulkChangedLine, chequeBulkDoneLine, chequeBulkSkipLines, type ChequeBulkResult,
} from '@timely/domain';
import { FormNotice } from '../screens/AuthLayout';
import { useFormatters } from '../book/useFormatters';
import { bulkChequeStatus } from './useCheques';

/** Each move, with the words for its button, its question ("Mark 3 cheques cleared") and what happened. */
export const BULK_MOVES: readonly { status: ChequeStatus; label: string; phrase: (what: string) => string; done: string }[] = [
    { status: ChequeStatus.Cleared, label: 'Mark cleared', phrase: what => `Mark ${what} cleared`, done: 'Marked cleared' },
    { status: ChequeStatus.Hold, label: 'Put on hold', phrase: what => `Put ${what} on hold`, done: 'Put on hold' },
    { status: ChequeStatus.Bounced, label: 'Mark bounced', phrase: what => `Mark ${what} bounced`, done: 'Marked bounced' },
    { status: ChequeStatus.Pending, label: 'Back in hand', phrase: what => `Put ${what} back in hand`, done: 'Back in hand' },
];

const chequeWords = (n: number) => `${n} ${n === 1 ? 'cheque' : 'cheques'}`;

/**
 * Many cheques moved together (0033) — the day's deposits cleared from one
 * bank statement, a batch returned by the bank.
 *
 * The dialog opens by asking the database what would happen: each cheque is
 * checked on its own, for the person's scope and for whether the move is one
 * a bank allows from where the cheque stands (a cleared cheque cannot be put
 * on hold). Only then does a button say how many will move. Each cheque moved
 * gets the same history line a single change writes.
 */
export function ChequeBulkDialog({ workspaceId, ids, status, onClose, onDone }: {
    workspaceId: string;
    ids: readonly string[];
    status: ChequeStatus;
    onClose: () => void;
    onDone: (message: string) => Promise<void>;
}) {
    const { money } = useFormatters();
    const move = BULK_MOVES.find(m => m.status === status)!;
    const [checked, setChecked] = useState<ChequeBulkResult | null>(null);
    const [busy, setBusy] = useState(true);
    const [problem, setProblem] = useState<string | null>(null);

    useEffect(() => {
        let current = true;
        void bulkChequeStatus(workspaceId, ids, status, true)
            .then(result => { if (current) setChecked(result); })
            .catch((failed: Error) => { if (current) setProblem(failed.message); })
            .finally(() => { if (current) setBusy(false); });
        return () => { current = false; };
    }, [workspaceId, ids, status]);

    const confirm = async () => {
        if (busy || !checked || checked.changed === 0) return;
        setBusy(true);
        setProblem(null);
        try {
            const result = await bulkChequeStatus(workspaceId, ids, status, false);
            await onDone(chequeBulkDoneLine(result, move.done));
        } catch (failed) {
            setProblem(failed instanceof Error ? failed.message : 'That did not work.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <DialogShell
            title={`${move.phrase(chequeWords(ids.length))}?`}
            subtitle="Each cheque is checked first. Nothing moves until you confirm."
            size="sm"
            onClose={onClose}
            closeOnEscape={!busy}
            onSubmit={e => { e.preventDefault(); void confirm(); }}
            footer={
                <>
                    <Button type="button" variant="quiet" disabled={busy && checked !== null} onClick={onClose}>Cancel</Button>
                    <Button type="submit" variant="primary" busy={busy} disabled={!checked || checked.changed === 0}>
                        {busy ? (checked ? 'Working…' : 'Checking…')
                            : checked ? move.phrase(chequeWords(checked.changed)) : move.label}
                    </Button>
                </>
            }
        >
            {problem && <div className="mb-4"><FormNotice tone="error">{problem}</FormNotice></div>}
            {!checked && !problem && <p className="text-secondary text-label-3" role="status">Checking each cheque…</p>}
            {checked && (
                <div role="status">
                    <p className="text-body font-semibold text-label">{chequeBulkChangedLine(checked)}</p>
                    {checked.changed > 0 && (
                        <p className="text-secondary text-label-3 mt-0.5">
                            Together <span className="num text-label-2">{money.exact(checked.amount)}</span>.
                        </p>
                    )}
                    {chequeBulkSkipLines(checked).length > 0 && (
                        <ul className="mt-2 space-y-1 text-secondary text-label-2 list-disc pl-5">
                            {chequeBulkSkipLines(checked).map(line => <li key={line}>{line}</li>)}
                        </ul>
                    )}
                </div>
            )}
        </DialogShell>
    );
}
